import React, { useState, useRef, useEffect, useCallback, PropsWithChildren, ReactNode } from 'react';
import './HorizontalActionList.scss';
import { classNames } from 'primereact/utils';

type HorizontalActionListProps = PropsWithChildren<{
	itemsClassName?: string;
	swipeDisabled?: boolean;
	onSwipeAction?: () => void;
	maxSwipeWidth?: number;
	mobileBreakpoint?: number;
	renderAction?: (swipePerc: number) => ReactNode;
}>;

export const HorizontalActionList: React.FC<HorizontalActionListProps> = ({
	children,
	swipeDisabled = false,
	itemsClassName,
	onSwipeAction,
	maxSwipeWidth = 100,
	mobileBreakpoint = 768,
	renderAction,
}) => {
	const [isMobile, setIsMobile] = useState(window.innerWidth < mobileBreakpoint);
	const [isAtEnd, setIsAtEnd] = useState(false);
	const [translateX, setTranslateX] = useState(0);
	const [swipeWidth, setSwipeWidth] = useState(0);
	const [isSwiping, setIsSwiping] = useState(false);

	const scrollRef = useRef<HTMLDivElement>(null);
	const startXRef = useRef<number>(0);
	const startTranslateXRef = useRef<number>(0);
	const startSwipeWidthRef = useRef<number>(0);

	useEffect(() => {
		const handleResize = () => {
			const mobile = window.innerWidth < mobileBreakpoint;
			setIsMobile(mobile);
			if (!mobile) {
				setTranslateX(0);
				setSwipeWidth(0);
				setIsSwiping(false);
			}
		};

		window.addEventListener('resize', handleResize);
		handleResize();

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [mobileBreakpoint]);

	const checkIsAtEnd = useCallback(() => {
		if (scrollRef.current) {
			const { scrollLeft, clientWidth, scrollWidth } = scrollRef.current;
			setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
		}
	}, []);

	useEffect(() => {
		checkIsAtEnd();
		const scrollEl = scrollRef.current;
		scrollEl?.addEventListener('scroll', checkIsAtEnd);
		return () => {
			scrollEl?.removeEventListener('scroll', checkIsAtEnd);
		};
	}, [children, checkIsAtEnd]);

	// Touch events dla swipe
	const handleTouchStart = useCallback(
		(event: React.TouchEvent) => {
			if (!isMobile || !isAtEnd || swipeDisabled) return;

			startXRef.current = event.touches[0].clientX;
			startTranslateXRef.current = translateX;
			startSwipeWidthRef.current = swipeWidth;
			setIsSwiping(true);
		},
		[swipeDisabled, isMobile, isAtEnd, translateX, swipeWidth]
	);

	const handleTouchMove = useCallback(
		(event: React.TouchEvent) => {
			if (!isSwiping || !isMobile || !isAtEnd || swipeDisabled) return;

			const currentX = event.touches[0].clientX;
			const deltaX = currentX - startXRef.current;

			if (deltaX < 0 && Math.abs(deltaX) <= maxSwipeWidth) {
				const newTranslateX = startTranslateXRef.current + deltaX;
				const newSwipeWidth = Math.min(Math.max(0, startSwipeWidthRef.current - deltaX), maxSwipeWidth);

				setTranslateX(newTranslateX);
				setSwipeWidth(newSwipeWidth);
			}
		},
		[swipeDisabled, isSwiping, isMobile, isAtEnd, maxSwipeWidth]
	);

	const handleTouchEnd = useCallback(() => {
		if (!isSwiping) return;
		setIsSwiping(false);
		if (swipeWidth >= maxSwipeWidth * 0.9) {
			onSwipeAction?.();
			const scrollEl = scrollRef.current;
			scrollEl?.scrollTo({ left: 0 });
		}
		setSwipeWidth(0);
		setTranslateX(0);
	}, [isSwiping, swipeWidth, maxSwipeWidth, onSwipeAction]);

	const swipePerc = Number((swipeWidth / maxSwipeWidth).toFixed(2));

	return (
		<div className="horizontal-list-container">
			<div
				ref={scrollRef}
				className={`horizontal-list ${isSwiping ? 'swiping' : ''}`}
				style={{ transform: `translateX(${translateX}px)` }}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
			>
				<div className={classNames('items-wrapper', itemsClassName)}>{children}</div>
			</div>
			{isMobile && renderAction && (
				<div
					className="swipe-element"
					style={{
						width: `${swipeWidth}px`,
						transition: isSwiping ? 'none' : 'width 0.3s ease-out',
					}}
				>
					<div className="swipe-content">{renderAction(swipePerc)}</div>
				</div>
			)}
		</div>
	);
};
